import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { FIELD, GHOST, inr, compact } from './shared';

export const TIERS = ['Nano', 'Micro', 'Macro', 'Mega'];
const PLATFORMS = ['Instagram', 'YouTube', 'Facebook', 'Snapchat', 'X', 'LinkedIn', 'Moj', 'Josh'];
const COLS = [
  ['creator_name', 'Creator Name', 'text'], ['instagram_handle', 'Instagram Handle', 'text'], ['followers', 'Followers', 'number'],
  ['tier', 'Tier', 'tier'], ['platform', 'Platform', 'platform'], ['deliverable', 'Deliverable', 'text'],
  ['cost', 'Cost (₹)', 'number'], ['agency_fee', 'Agency Fee (₹)', 'number'],
];
export const emptyRow = () => ({ creator_name: '', instagram_handle: '', followers: null, tier: '', platform: 'Instagram', deliverable: 'Reel', cost: null, agency_fee: null });

export const sheetTotals = (rows, planFee) => {
  const creator = rows.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const fee = rows.reduce((s, r) => s + (Number(r.agency_fee) || 0), 0) + (Number(planFee) || 0);
  return { creator, fee, total: creator + fee };
};

const Cell = ({ col, value, onChange, testid }) => {
  const [key, , kind] = col;
  const cls = `${FIELD} h-8 text-xs ${kind === 'platform' ? 'min-w-[124px]' : 'min-w-[90px]'}`;
  if (kind === 'tier') return <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid}><option value="">Auto</option>{TIERS.map((t) => <option key={t}>{t}</option>)}</select>;
  if (kind === 'platform') return <select value={value || 'Instagram'} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testid}>{PLATFORMS.map((t) => <option key={t}>{t}</option>)}</select>;
  return <input type={kind} min={kind === 'number' ? 0 : undefined} value={value ?? ''} placeholder={key === 'agency_fee' ? 'optional' : ''}
    onChange={(e) => onChange(kind === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)} className={cls} data-testid={testid} />;
};

const display = (key, v) => (key === 'cost' || key === 'agency_fee' ? (v == null || v === '' ? '—' : inr(v)) : key === 'followers' ? compact(v) : key === 'instagram_handle' && v ? `@${v}` : v || '—');

export const CreatorSheetTable = ({ rows, setRows, planFee = 0, editable = false, testid = 'creator-sheet' }) => {
  const t = sheetTotals(rows, planFee);
  const update = (i, key, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  const th = 'px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 whitespace-nowrap';
  const foot = 'px-3 py-2.5 text-sm';
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-black/[0.02] dark:bg-white/[0.02]">{COLS.map(([k, h]) => <th key={k} className={th}>{h}</th>)}{editable && <th className={th} />}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-black/[0.06] dark:border-white/5" data-testid={`${testid}-row-${i}`}>
                {COLS.map((c) => (
                  <td key={c[0]} className="px-3 py-2 text-sm text-[#0F0F0F] dark:text-white whitespace-nowrap">
                    {editable ? <Cell col={c} value={r[c[0]]} onChange={(v) => update(i, c[0], v)} testid={`${testid}-${c[0]}-${i}`} /> : display(c[0], r[c[0]])}
                  </td>
                ))}
                {editable && <td className="px-2"><button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-1.5 text-[#6B6B6B] hover:text-red-500 transition-colors" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button></td>}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={COLS.length + 1} className="px-4 py-8 text-center text-sm text-[#6B6B6B]">No creators yet.</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t border-black/[0.08] dark:border-white/10 bg-black/[0.015]"><td colSpan={6} className={`${foot} text-[#6B6B6B]`}>Creator costs ({rows.length})</td><td colSpan={editable ? 3 : 2} className={`${foot} font-mono`} data-testid={`${testid}-creator-total`}>{inr(t.creator)}</td></tr>
            <tr className="border-t border-black/[0.06] dark:border-white/5 bg-black/[0.015]"><td colSpan={6} className={`${foot} text-[#6B6B6B]`}>Agency fee</td><td colSpan={editable ? 3 : 2} className={`${foot} font-mono`} data-testid={`${testid}-fee-total`}>{inr(t.fee)}</td></tr>
            <tr className="border-t border-black/[0.08] dark:border-white/10 bg-[#FF451A]/[0.05]"><td colSpan={6} className={`${foot} font-semibold text-[#0F0F0F] dark:text-white`}>Total budget</td><td colSpan={editable ? 3 : 2} className={`${foot} font-mono font-bold text-[#FF451A]`} data-testid={`${testid}-grand-total`}>{inr(t.total)}</td></tr>
          </tfoot>
        </table>
      </div>
      {editable && <button type="button" onClick={() => setRows([...rows, emptyRow()])} className={GHOST} data-testid={`${testid}-add-row`}><Plus size={12} /> Add creator</button>}
    </div>
  );
};
